// src/services/functionalStructure.service.js
import { api } from './api';

class FunctionalStructureService {
    /**
     * Get active functional structure
     */
    async getActive() {
        try {
            const response = await api.get('/api/functional-structure');
            return response.data.data || null;
        } catch (error) {
            console.error('Error fetching active functional structure:', error);
            return this.getLocalStorageStructure();
        }
    }

    /**
     * Get all versions of functional structure
     */
    async getAll() {
        try {
            const response = await api.get('/api/functional-structure/all');
            return response.data.data || [];
        } catch (error) {
            console.error('Error fetching all functional structures:', error);
            return [];
        }
    }

    /**
     * Get specific version
     */
    async getVersion(version) {
        try {
            const response = await api.get(`/api/functional-structure/version/${version}`);
            return response.data.data || null;
        } catch (error) {
            console.error('Error fetching version:', error);
            throw error;
        }
    }

    /**
     * Create or update functional structure
     */
    async upsert(structureData) {
        try {
            const response = await api.post('/api/functional-structure', {
                structureData
            });
            return response.data;
        } catch (error) {
            console.error('Error saving functional structure:', error);
            throw error;
        }
    }

    /**
     * Activate specific version
     */
    async activateVersion(version) {
        try {
            const response = await api.put(`/api/functional-structure/version/${version}/activate`);
            return response.data;
        } catch (error) {
            console.error('Error activating version:', error);
            throw error;
        }
    }

    /**
     * Get document templates
     */
    async getTemplates() {
        try {
            const response = await api.get('/api/functional-structure/templates');
            return response.data.data || [];
        } catch (error) {
            console.error('Error fetching templates:', error);
            return [];
        }
    }

    /**
     * Get selected templates
     */
    async getSelectedTemplates() {
        try {
            const response = await api.get('/api/functional-structure/templates/selected');
            return response.data.data || [];
        } catch (error) {
            console.error('Error fetching selected templates:', error);
            return [];
        }
    }

    /**
     * Create template
     */
    async createTemplate(templateData) {
        try {
            const response = await api.post('/api/functional-structure/templates', templateData);
            return response.data;
        } catch (error) {
            console.error('Error creating template:', error);
            throw error;
        }
    }

    /**
     * Update template
     */
    async updateTemplate(id, templateData) {
        try {
            const response = await api.put(`/api/functional-structure/templates/${id}`, templateData);
            return response.data;
        } catch (error) {
            console.error('Error updating template:', error);
            throw error;
        }
    }

    /**
     * Delete template
     */
    async deleteTemplate(id) {
        try {
            const response = await api.delete(`/api/functional-structure/templates/${id}`);
            return response.data;
        } catch (error) {
            console.error('Error deleting template:', error);
            throw error;
        }
    }

    /**
     * Fallback: Get functional structure from localStorage
     */
    getLocalStorageStructure() {
        try {
            const stored = localStorage.getItem('functionalStructure');
            if (!stored) return null;

            const parsed = JSON.parse(stored);
            return {
                structure_data: {
                    legalEntities: parsed.legalEntities || [],
                    expandedEntities: parsed.expandedEntities || {}
                }
            };
        } catch (error) {
            console.error('Error loading functional structure from localStorage:', error);
            return null;
        }
    }

    /**
     * Save to localStorage for offline access
     */
    saveToLocalStorage(structureData) {
        try {
            localStorage.setItem('functionalStructure', JSON.stringify(structureData));
        } catch (error) {
            console.error('Error saving functional structure to localStorage:', error);
        }
    }
}

export default new FunctionalStructureService();
